/**
 * 🔒 UTILIDADES DE SEGURIDAD - LegalPlus
 * 
 * Sistema de encriptación y protección de datos en el cliente
 * IMPORTANTE: Esta NO es seguridad real de backend, solo ofuscación
 * para dificultar ataques básicos de XSS. La seguridad real está en el backend.
 */

import CryptoJS from 'crypto-js';

// Clave de encriptación (en producción debería venir de variables de entorno)
// NOTA: La seguridad real viene del backend con HttpOnly cookies
const ENCRYPTION_KEY = process.env.REACT_APP_STORAGE_KEY || 'LegalPlus2026SecureKey!@#';

/**
 * Encripta datos antes de almacenarlos
 * @param {any} data - Datos a encriptar
 * @returns {string} Datos encriptados en base64
 */
export const encryptData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Error al encriptar datos:', error);
    return null;
  }
};

/**
 * Desencripta datos almacenados
 * @param {string} encryptedData - Datos encriptados
 * @returns {any} Datos desencriptados o null
 */
export const decryptData = (encryptedData) => {
  try {
    if (!encryptedData) return null;
    
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) return null;
    
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Error al desencriptar datos:', error);
    return null;
  }
};

/**
 * Almacenamiento seguro en localStorage con encriptación
 */
export const secureStorage = {
  /**
   * Guarda datos encriptados
   * @param {string} key - Clave de almacenamiento
   * @param {any} data - Datos a guardar
   */
  setItem: (key, data) => {
    try {
      const encrypted = encryptData(data);
      if (encrypted) {
        localStorage.setItem(key, encrypted);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error al guardar en storage:', error);
      return false;
    }
  },

  /**
   * Recupera y desencripta datos
   * @param {string} key - Clave de almacenamiento
   * @returns {any} Datos desencriptados o null
   */
  getItem: (key) => {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;
      return decryptData(encrypted);
    } catch (error) {
      console.error('Error al leer del storage:', error);
      return null;
    }
  },

  /**
   * Elimina un item
   * @param {string} key - Clave a eliminar
   */
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error al eliminar del storage:', error);
      return false;
    }
  },

  /**
   * Limpia todo el storage (logout completo)
   */
  clear: () => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error al limpiar storage:', error);
      return false;
    }
  }
};

/**
 * Sanitiza strings para prevenir XSS
 * @param {string} str - String a sanitizar
 * @returns {string} String sanitizado
 */
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return str.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Sanitiza objetos recursivamente
 * @param {any} data - Datos a sanitizar
 * @returns {any} Datos sanitizados
 */
export const sanitizeObject = (data) => {
  if (typeof data === 'string') {
    return sanitizeString(data);
  }
  
  if (Array.isArray(data)) {
    return data.map(sanitizeObject);
  }
  
  if (data !== null && typeof data === 'object') {
    const sanitized = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        sanitized[key] = sanitizeObject(data[key]);
      }
    }
    return sanitized;
  }
  
  return data;
};

/**
 * Valida y limpia inputs de usuario
 * @param {string} input - Input a validar
 * @param {string} type - Tipo de validación (email, text, number)
 * @returns {object} { isValid: boolean, cleaned: string }
 */
export const validateInput = (input, type = 'text') => {
  let cleaned = String(input).trim();
  let isValid = true;

  switch (type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      isValid = emailRegex.test(cleaned);
      cleaned = sanitizeString(cleaned);
      break;
      
    case 'number':
      cleaned = cleaned.replace(/[^0-9]/g, '');
      isValid = cleaned.length > 0;
      break;
      
    case 'alphanumeric':
      cleaned = cleaned.replace(/[^a-zA-Z0-9]/g, '');
      isValid = cleaned.length > 0;
      break;
      
    case 'text':
    default:
      cleaned = sanitizeString(cleaned);
      isValid = cleaned.length > 0;
      break;
  }

  return { isValid, cleaned };
};

/**
 * Genera un hash simple de un string (NO para passwords)
 * Solo para identificadores de sesión, etc.
 * @param {string} str - String a hashear
 * @returns {string} Hash
 */
export const simpleHash = (str) => {
  return CryptoJS.SHA256(str).toString();
};

/**
 * Verifica si el usuario está autenticado de forma segura
 * Comprueba integridad de datos
 * @returns {boolean}
 */
export const verifyAuthIntegrity = () => {
  try {
    const userData = secureStorage.getItem('userData');
    const isAuth = secureStorage.getItem('isAuthenticated');
    
    // Verificaciones básicas de integridad
    if (!userData || !isAuth) return false;
    if (!userData.id || !userData.rol) return false;
    
    // Verificar que el usuario no haya sido manipulado
    const expectedHash = simpleHash(JSON.stringify(userData));
    const storedHash = secureStorage.getItem('userHash');
    
    if (storedHash && storedHash !== expectedHash) {
      console.warn('⚠️ Integridad de sesión comprometida');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error verificando integridad:', error);
    return false;
  }
};

/**
 * Guarda datos de usuario con verificación de integridad
 * @param {object} userData - Datos del usuario
 */
export const saveUserDataSecurely = (userData) => {
  try {
    // Guardar datos encriptados
    secureStorage.setItem('userData', userData);
    secureStorage.setItem('isAuthenticated', true);
    
    // Guardar hash para verificación de integridad
    const userHash = simpleHash(JSON.stringify(userData));
    secureStorage.setItem('userHash', userHash);
    
    return true;
  } catch (error) {
    console.error('Error guardando usuario:', error);
    return false;
  }
};

/**
 * Limpia toda la sesión de forma segura
 */
export const clearSession = () => {
  secureStorage.removeItem('userData');
  secureStorage.removeItem('isAuthenticated');
  secureStorage.removeItem('userHash');
};

/**
 * Ofusca información sensible para logs
 * @param {string} str - String a ofuscar
 * @param {number} visibleChars - Caracteres visibles al inicio/fin
 * @returns {string}
 */
export const obfuscate = (str, visibleChars = 3) => {
  if (!str || str.length <= visibleChars * 2) return '***';
  return `${str.substring(0, visibleChars)}***${str.substring(str.length - visibleChars)}`;
};

/**
 * Detecta intentos de inyección SQL/NoSQL básicos
 * @param {string} input - Input a verificar
 * @returns {boolean} true si es sospechoso
 */
export const detectInjection = (input) => {
  if (typeof input !== 'string') return false;
  
  const suspiciousPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|EXEC|SCRIPT)\b)/gi,
    /(--|#|\/\*|\*\/|;)/g,
    /(\$where|\$ne|\$gt|\$lt)/gi
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(input));
};

/**
 * Rate limiting simple en el cliente (complementa el del backend)
 */
class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 60000) {
    this.attempts = new Map();
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  /**
   * Verifica si se permite una acción
   * @param {string} key - Identificador de la acción
   * @returns {boolean}
   */
  check(key) {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Limpiar intentos antiguos
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return true;
  }

  /**
   * Resetea el contador para una clave
   * @param {string} key
   */
  reset(key) {
    this.attempts.delete(key);
  }
}

export const loginRateLimiter = new RateLimiter(5, 60000); // 5 intentos por minuto

// Export por defecto
const securityUtils = {
  secureStorage,
  sanitizeString,
  sanitizeObject,
  validateInput,
  verifyAuthIntegrity,
  saveUserDataSecurely,
  clearSession,
  obfuscate,
  detectInjection,
  loginRateLimiter
};

export default securityUtils;
